import React from 'react';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CSSVariables, FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button as KendoButton, ButtonHandle, ButtonProps as KendoButtonProps } from '@progress/kendo-react-buttons';
import { Tooltip, TooltipPosition } from '@progress/kendo-react-tooltip';
import cn from 'clsx';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';

export interface ButtonTooltip {
    text: string;
    position?: TooltipPosition;
}

export interface ButtonProps extends KendoButtonProps, React.RefAttributes<ButtonHandle | null> {
    'data-testid': string;
    primary?: boolean;
    carousel?: boolean;
    faIcon?: IconDefinition | IconProp;
    faIconStyle?: React.CSSProperties & CSSVariables;
    faIconPosition?: 'start' | 'end';
    ignoreReadonlyCtx?: boolean;
    tooltip?: ButtonTooltip;
    disableFocus?: boolean;
}

const Button = ({
    children,
    primary,
    className,
    faIcon,
    faIconPosition = 'start',
    faIconStyle,
    disabled,
    carousel,
    ignoreReadonlyCtx,
    tooltip,
    disableFocus,
    ...props
}: ButtonProps) => {
    let content = children;
    const { readOnly } = useReadOnlyContext();
    if (faIcon) {
        content = (
            <span className="k-d-flex k-gap-2 k-align-items-center">
                {faIconPosition === 'start' && <FontAwesomeIcon icon={faIcon} style={faIconStyle ?? { height: 17 }} />}
                {content}
                {faIconPosition === 'end' && <FontAwesomeIcon icon={faIcon} style={faIconStyle ?? { height: 17 }} />}
            </span>
        );
    }

    const button = (
        <KendoButton
            themeColor={primary ? 'primary' : undefined}
            className={cn(
                {
                    'tri-button-carousel': carousel,
                },
                className,
            )}
            disabled={disabled || (!ignoreReadonlyCtx && readOnly)}
            {...props}
            onClick={(event) => {
                if (disableFocus) {
                    const active = document.activeElement;
                    if (active instanceof HTMLElement) {
                        active.blur();
                    }
                }
                props.onClick && props.onClick(event);
            }}
            onFocus={(event) => {
                if (disableFocus) {
                    const active = document.activeElement;
                    if (active instanceof HTMLElement && !event.relatedTarget) {
                        active.blur();
                    }
                }
                props.onFocus && props.onFocus(event);
            }}
            rounded={props.rounded || 'medium'}
            fillMode={props.fillMode || 'solid'}
        >
            {content}
        </KendoButton>
    );

    if (tooltip) {
        return (
            <Tooltip anchorElement="target" position={tooltip.position ?? 'auto'} parentTitle>
                <div className="wrapper" title={tooltip.text}>
                    {button}
                </div>
            </Tooltip>
        );
    }

    return button;
};

export default Button;
